import React, { ComponentProps, useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getAvailableDurationPackageOptions, getDurationPackageOptions, OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import TextButton from 'components/Luxkit/Button/TextButton'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'

import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { generateAccommodationCheckoutItems, generateVillaCheckoutItem } from 'lib/checkout/accommodation/cart'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { useCreateTripItemBatch } from 'tripPlanner/hooks/api/tripItem'
import GeoContext from 'contexts/geoContext'
import { createLEHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/leHotel'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getUpgradeablePackage } from '../utils'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { FlightViewTypes } from 'constants/flight'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { getUpgradeItemContext, upgradeEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PackageUpgradeModal from '../PackageUpgrade/PackageUpgradeModal'
import moment from 'moment'
import { checkoutStateIsReady } from 'middlewares/analyticsMiddleware'
import getSelectedLoyaltyTargetUpgradePackage from '../offerPageUtils/getSelectedLoyaltyTargetUpgradePackage'

interface GenerateCartOptions {
  packageOption: OfferPackageOptionView | undefined,
  flights: boolean,
  upgradePackageOption?: OfferPackageOptionView,
  originalPackageOption?: OfferPackageOptionView,
  loyaltyUpgradeTargetPackageOption?: OfferPackageOptionView,
}

interface Props extends ComponentProps<typeof TextButton> {
  airport?: App.Airport;
  checkIn?: string;
  isLoggedIn: boolean;
  offer: App.Offer;
  packageOptions: Array<OfferPackageOptionView>;
  availablePackageOptions: Array<OfferPackageOptionView>;
  packageView: OfferPackageOptionView;
  rooms: Array<App.RoomOccupants>;
  offerAvailableRates?: App.OfferAvailableRatesByOccupancy;
  onTransitionToBooking?: () => void;
  onSubmit?: () => void;
  tripPlannerTripId?: string;
  reserveForZero?: boolean;
  overrideRooms?: Array<App.RoomOccupants | App.Occupants>;
  onProceedToBooking: (items: Array<App.Checkout.AnyItem>) => void;
  upgradeEnabled?: boolean;
  loyaltyTargetRoomUpgradesSelected?: App.LoyaltyTargetRoomUpgradesSelected;
}

function OfferCheckoutButton(props: Props) {
  const {
    children,
    packageView,
    rooms,
    isLoggedIn,
    checkIn,
    packageOptions,
    availablePackageOptions,
    offer,
    airport,
    offerAvailableRates,
    onTransitionToBooking,
    onSubmit,
    tripPlannerTripId,
    onProceedToBooking,
    upgradeEnabled = true,
    loyaltyTargetRoomUpgradesSelected,
    ...rest
  } = props
  const isMobile = useScreenSizeOnly('mobile')
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  // We are passed a package view, but that's not always guaranteed to be the 'latest' one
  // e.g. a `selectedPackageView` doesn't include price updates based on date selection (maybe there was a surcharge?)
  // So to get around this, we always use the 'latest' calculated view for the unique key we're given
  const latestPackageView = useMemo(() => packageOptions.find(option => option.package.uniqueKey === packageView.package.uniqueKey), [packageOptions, packageView.package.uniqueKey])

  const generateCart = useCallback(({
    packageOption,
    flights,
    upgradePackageOption,
    originalPackageOption,
    loyaltyUpgradeTargetPackageOption,
  }: GenerateCartOptions) => {
    if (!packageOption) { return }
    const checkOut = checkIn ? moment(checkIn).add(packageOption.package.duration, 'days').format(ISO_DATE_FORMAT) : undefined
    const dates = { checkIn, checkOut }

    let accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem> = []
    if (offer.type !== 'rental') {
      accommodationItems = generateAccommodationCheckoutItems({
        offer,
        packageOption,
        dates,
        roomOccupants: rooms,
        currencyCode: currentCurrency,
        hasLoyaltyBenefitRoomUpgrade: !!loyaltyUpgradeTargetPackageOption,
      })
    }

    let villaItems: Array<App.Checkout.VillaItem> = []
    if (offer.type === 'rental') {
      villaItems = [generateVillaCheckoutItem(offer, packageOption, dates, rooms[0], currentCurrency)]
    }

    const flightItems: Array<App.Checkout.FlightItem> = []
    if (flights && airport) {
      const flightOccupants = countOccupantsForFlights(rooms)
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: airport.code,
        destinationAirportCode: offer.flightDestinationPort,
        bundledItemIds: accommodationItems.map(item => item.itemId),
        occupants: flightOccupants,
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        passengers: generatePassengersFromOccupants(flightOccupants),
        quotedFare: packageOption.flightPrice / rooms.length,
      }))
    }

    if (upgradePackageOption && originalPackageOption && accommodationItems.length === 1) {
      checkoutStateIsReady().then(() => Analytics.trackEvent(
        upgradeEvent({
          hasUpgraded: packageOption.package.uniqueKey === upgradePackageOption.package.uniqueKey,
          chosenCartItemId: accommodationItems[0].itemId,
        },
        getUpgradeItemContext(originalPackageOption, upgradePackageOption, accommodationItems[0]),
        ['hotel'],
        ),
      ),
      )
    }
    onProceedToBooking([...accommodationItems, ...villaItems, ...flightItems])
  }, [checkIn, offer, airport, onProceedToBooking, rooms, currentCurrency])

  const onBookNow = useCallback((packageOption: OfferPackageOptionView, flights: boolean) => {
    // we're booking, close the filters
    offerPageDispatch({ type: OfferPageActions.closeFilter })
    // also, if we're currently being called from *inside* a modal - close it
    modalElementContext?.resolve()

    const saveToCurrentTrip = (packageOption: OfferPackageOptionView) => {
      if (currentTrip) {
        const checkOut = checkIn ? moment(checkIn).add(packageOption.package.duration, 'days').format(ISO_DATE_FORMAT) : undefined
        const payload = createLEHotelItemPayload({
          offer,
          pkg: packageOption.package,
          startDate: checkIn,
          endDate: checkOut,
          occupancies: rooms.map(room => ({
            adults: room.adults,
            children: room.children,
            infants: room.infants,
            childrenAge: room.childrenAge,
          })),
          currentRegionCode,
          currencyCode: currentCurrency,
          price: packageOption.hotelTotal,
          value: packageOption.hotelValue + packageOption.propertyFees,
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }
    }

    const loyaltyTargetUpgradePackageSelected = getSelectedLoyaltyTargetUpgradePackage(
      packageOption,
      availablePackageOptions,
      loyaltyTargetRoomUpgradesSelected,
    )

    if (loyaltyTargetUpgradePackageSelected) {
      fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, loyaltyTargetUpgradePackageSelected?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
      generateCart({
        packageOption,
        flights,
        loyaltyUpgradeTargetPackageOption: loyaltyTargetUpgradePackageSelected,
      })
      saveToCurrentTrip(loyaltyTargetUpgradePackageSelected)
      return
    }

    let upgradeAvailable: OfferPackageOptionView | undefined
    if (
      upgradeEnabled &&
      isEnabledForFeature(config.PACKAGE_UPGRADE_MODAL_ENABLED_LIST, offer.type) &&
      availablePackageOptions.length > 0
    ) {
      const checkInMoment = checkIn ? moment(checkIn) : undefined
      upgradeAvailable = getUpgradeablePackage(availablePackageOptions, packageOption, checkInMoment, isLpcHotelOffer(offer), offer.property?.timezoneOffset)
    }

    if (upgradeAvailable) {
      trackEvent('UPGRADE_MODAL_OPENED')
      Analytics.trackClientEvent({
        subject: 'upgrade_modal',
        action: 'opened',
        category: 'logging',
        type: 'nonInteraction',
      })
      showModal<OfferPackageOptionView | undefined>(<PackageUpgradeModal
        upgradePackageView={upgradeAvailable}
        basePackageView={packageOption}
        hasFlights={flights}
        offer={offer}
        checkIn={checkIn}
      />,
      ).then((resolvedPackageOption?: OfferPackageOptionView) => {
        if (resolvedPackageOption) {
          fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, resolvedPackageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
          generateCart({
            packageOption: resolvedPackageOption,
            flights,
            upgradePackageOption: upgradeAvailable,
            originalPackageOption: packageOption,
          })
          saveToCurrentTrip(resolvedPackageOption)
        }
      })
      return
    }

    fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, packageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
    generateCart({ packageOption, flights })
    saveToCurrentTrip(packageOption)
  }, [
    offerPageDispatch,
    modalElementContext,
    upgradeEnabled,
    offer,
    availablePackageOptions,
    loyaltyTargetRoomUpgradesSelected,
    currentCurrency,
    rooms,
    generateCart,
    currentTrip,
    checkIn,
    currentRegionCode,
    createTripItems,
    trackEvent,
    showModal,
  ])

  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)

  const onBookNowClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    if (!latestPackageView) return
    onSubmit?.()

    const isFlightBuyType = e.currentTarget.dataset.buytype === 'flights'

    if (invalidRoomSelection) {
      e.preventDefault()
      // if we're currently being called from *inside* a modal - close it
      modalElementContext?.resolve()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: latestPackageView?.package.roomType,
        flights: isFlightBuyType,
        selectedPackageView: latestPackageView,
      })
      return
    }

    if (!checkIn) {
      e.preventDefault()
      // if we're currently being called from *inside* a modal - close it
      modalElementContext?.resolve()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: latestPackageView?.package.roomType,
        flights: isFlightBuyType,
        selectedPackageView: latestPackageView,
      })

      return
    }

    if (!isLoggedIn && !offer.walledGarden) {
      e.preventDefault()

      if (!isMobile) offerPageDispatch({ type: OfferPageActions.closeFilter })

      const hotelOfferDetails: App.AccountModalOfferDetails | undefined = latestPackageView && {
        title: offer.property?.name || latestPackageView.package.name,
        duration: latestPackageView.package.duration,
        value: isFlightBuyType ? latestPackageView.hotelPrice + latestPackageView.flightPrice : latestPackageView.hotelPrice,
        imageId: latestPackageView.package.roomType?.images[0].id,
        imageTitle: offer.property?.name || latestPackageView.package.name,
        discountPercentage: isFlightBuyType ? 0 : latestPackageView.hotelDiscountPercent,
        isBundledWithFlights: isFlightBuyType,
        offer,
        packageView: latestPackageView,
      }
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal
        initialMode="login"
        hotelOfferDetails={hotelOfferDetails}
      />)

      if (result.loggedIn) {
        onBookNow(latestPackageView, isFlightBuyType)
      }
      return
    }

    onBookNow(latestPackageView, isFlightBuyType)
  }, [
    latestPackageView,
    onSubmit,
    invalidRoomSelection,
    checkIn,
    isLoggedIn,
    offer,
    onBookNow,
    modalElementContext,
    offerPageDispatch,
    isMobile,
    showModal,
  ])

  return (
    <TextButton {...rest} onClick={onBookNowClick}>
      {children}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  airport: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  offer: state.offer,
  offerAvailableRates: appState.offer.offerAvailableRates[state.offer.id],
  packageOptions: getDurationPackageOptions(state, appState),
  availablePackageOptions: getAvailableDurationPackageOptions(state, appState),
  rooms: ownProps.overrideRooms ?? state.rooms,
  tripPlannerTripId: appState.tripPlanner.currentTripId,
  loyaltyTargetRoomUpgradesSelected: state.loyaltyTargetRoomUpgradesSelected,
}))(OfferCheckoutButton)
